// l2b-test-engine.js — L2B Testing.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Exercises/L2B/L2BTestEngine.cs
//
// Pure state machine. Same architecture rule as L2BEngine: no DOM, no fetch,
// no audio. Mirrors the C# engine's public API one-for-one so a `git diff`
// against the Unity port stays readable.
//
// L2B Testing differs from Training in three ways:
//   • No mastery streaks — we count correct/incorrect over a fixed run
//   • No large-intro phase — every round is the tile grid
//   • Pool = the learner's "met universe" (MMUs they've previously trained
//     on); the engine receives that pool from the screen, not from the
//     content item
//
// Public API (matches L2BTestEngine.cs):
//   new L2BTestEngine(itemData, config)  → engine instance
//   start()                              → first L2BTestState
//   onTileSelected(conceptId)            → answer; returns Feedback state
//   onFeedbackComplete()                 → next state after feedback delay
//   onReplayPressed()                    → returns current state
//   snapshot()                           → current L2BTestState
//
// Events (CustomEvent on the engine):
//   "stateChanged" — fired any time state advances; detail = snapshot
//   "result"       — { correct, conceptId } after onTileSelected
//   "completed"    — fired when state.mode === "complete"

/** @typedef {import("../data/mmu-loader.js").Mmu} Mmu */
/** @typedef {import("./l2b-engine.js").L2BItemData} L2BItemData */

// ─────────────────────────────────────────────────────────────
// Models — mirror L2BTestEngine.cs / L2BModels.cs
// ─────────────────────────────────────────────────────────────

/**
 * @typedef {object} L2BTestConfig
 * @property {number} [numberOfInstances=8]    test length
 * @property {number} [passScore=70]           percentage required to pass
 * @property {number} [gridSize=8]             tile count
 * @property {number} [feedbackDelay=1.5]      seconds (informational)
 * @property {boolean} [showCorrectOnWrong=true]
 * @property {"audioOnly"|"textOnly"|"audioAndText"} [modality="audioOnly"]
 */

/**
 * @typedef {"tileGrid"|"feedback"|"complete"} TestPresentationMode
 */

/**
 * @typedef {object} L2BTestRoundResult
 * @property {string}   conceptId
 * @property {boolean}  wasCorrect
 * @property {string}   selectedConceptId
 * @property {string[]} distractorIds
 */

/**
 * @typedef {object} L2BTestState
 * @property {TestPresentationMode} mode
 * @property {L2BItemData|null} targetItem
 * @property {L2BItemData[]}    tiles
 * @property {boolean}          wasCorrect
 * @property {string}           correctConceptId
 * @property {string}           selectedConceptId
 * @property {number}           currentRound
 * @property {number}           totalRounds
 * @property {number}           correctCount
 * @property {number}           errorCount
 * @property {number}           accuracyPercent
 * @property {number}           passScore
 * @property {boolean}          passed
 * @property {L2BTestRoundResult[]} roundResults
 */

// ─────────────────────────────────────────────────────────────
// Engine — mirror L2BTestEngine.cs
// ─────────────────────────────────────────────────────────────

export class L2BTestEngine extends EventTarget {
  /**
   * @param {L2BItemData[]} metUniverseItems  All items the learner has met.
   * @param {L2BTestConfig} [config]
   */
  constructor(metUniverseItems, config = {}) {
    super();
    /** @type {Required<L2BTestConfig>} */
    this._config = {
      numberOfInstances:  config.numberOfInstances ?? 8,
      passScore:          config.passScore ?? 70,
      gridSize:           config.gridSize ?? 8,
      feedbackDelay:      config.feedbackDelay ?? 1.5,
      showCorrectOnWrong: config.showCorrectOnWrong ?? true,
      modality:           config.modality ?? "audioOnly",
    };
    if (this._config.numberOfInstances <= 0) {
      throw new Error("[l2b-test] numberOfInstances must be > 0");
    }

    /** @type {L2BItemData[]} */
    this._metUniverse = metUniverseItems.slice();
    /** @type {L2BItemData[]} */
    this._testList = this._selectTestItems(this._metUniverse, this._config.numberOfInstances);

    /** @type {L2BTestRoundResult[]} */
    this._results = [];
    this._roundIndex = 0;
    this._correctCount = 0;
    this._errorCount = 0;
    /** @type {L2BItemData|null} */ this._currentTarget = null;
    /** @type {L2BItemData[]} */    this._currentTiles = [];

    /** @type {L2BTestState|null} */ this._lastState = null;
  }

  // ─── Public API ───────────────────────────────────────────

  start() {
    if (this._testList.length === 0) {
      return this._publish(this._buildCompleteState());
    }
    return this._publish(this._presentRound());
  }

  /** @param {string} selectedConceptId */
  onTileSelected(selectedConceptId) {
    if (this._currentTarget == null) return this._publish(this._buildCompleteState());

    const correct = selectedConceptId === this._currentTarget.conceptId;
    if (correct) this._correctCount++;
    else this._errorCount++;

    const distractors = this._currentTiles
      .filter((t) => t.conceptId !== this._currentTarget?.conceptId)
      .map((t) => t.conceptId);

    /** @type {L2BTestRoundResult} */
    const result = {
      conceptId: this._currentTarget.conceptId,
      wasCorrect: correct,
      selectedConceptId,
      distractorIds: distractors,
    };
    this._results.push(result);

    this.dispatchEvent(new CustomEvent("result", {
      detail: { correct, conceptId: this._currentTarget.conceptId },
    }));

    return this._publish(this._buildFeedbackState(correct, this._currentTarget.conceptId, selectedConceptId));
  }

  onFeedbackComplete() {
    this._roundIndex++;
    if (this._roundIndex >= this._testList.length) {
      return this._publish(this._buildCompleteState());
    }
    return this._publish(this._presentRound());
  }

  onReplayPressed() {
    return this._publish(this._buildTileGridState());
  }

  /** @returns {L2BTestState} */
  snapshot() {
    return this._lastState ?? this._buildCompleteState();
  }

  /**
   * Restore a previously persisted run state. The engine resumes at the
   * given round index with the supplied counts and history. Distractors
   * are re-rolled because they aren't persisted; that matches the iOS app
   * behaviour on re-entry.
   *
   * @param {{ roundIndex:number, correctCount:number, errorCount:number, results?:L2BTestRoundResult[] }} state
   */
  resume(state) {
    this._roundIndex   = Math.max(0, Math.min(state.roundIndex, this._testList.length));
    this._correctCount = state.correctCount | 0;
    this._errorCount   = state.errorCount   | 0;
    this._results      = (state.results ?? []).slice();
    if (this._roundIndex >= this._testList.length) {
      return this._publish(this._buildCompleteState());
    }
    return this._publish(this._presentRound());
  }

  // ─── Core Logic ───────────────────────────────────────────

  /**
   * Select numberOfInstances items from the met universe.
   * Matches L2BTestEngine.SelectTestItems(): shuffle, take, shuffle again.
   * @param {L2BItemData[]} pool
   * @param {number} count
   * @returns {L2BItemData[]}
   */
  _selectTestItems(pool, count) {
    const available = pool.slice();
    L2BTestEngine._shuffle(available);
    const take = Math.min(count, available.length);
    const selected = available.slice(0, take);
    L2BTestEngine._shuffle(selected);
    return selected;
  }

  _presentRound() {
    this._currentTarget = this._testList[this._roundIndex];
    this._currentTiles = this._buildTileList(this._currentTarget);
    return this._buildTileGridState();
  }

  /**
   * Target + (gridSize - 1) distractors from the full met universe.
   * @param {L2BItemData} target
   * @returns {L2BItemData[]}
   */
  _buildTileList(target) {
    /** @type {L2BItemData[]} */
    const tiles = [];

    if (this._metUniverse.length <= this._config.gridSize) {
      tiles.push(...this._metUniverse);
      if (!tiles.some((t) => t.conceptId === target.conceptId)) {
        tiles.push(target);
      }
    } else {
      tiles.push(target);
      const distractorPool = this._metUniverse.filter((m) => m.conceptId !== target.conceptId);
      L2BTestEngine._shuffle(distractorPool);
      let slotsRemaining = this._config.gridSize - 1;
      for (const it of distractorPool) {
        if (slotsRemaining <= 0) break;
        tiles.push(it);
        slotsRemaining--;
      }
    }

    L2BTestEngine._shuffle(tiles);
    return tiles;
  }

  // ─── State Builders ───────────────────────────────────────

  /** @returns {L2BTestState} */
  _buildTileGridState() {
    return {
      mode: "tileGrid",
      targetItem: this._currentTarget,
      tiles: this._currentTiles.slice(),
      wasCorrect: false,
      correctConceptId: "",
      selectedConceptId: "",
      currentRound: this._roundIndex,
      totalRounds: this._testList.length,
      correctCount: this._correctCount,
      errorCount: this._errorCount,
      accuracyPercent: this._accuracy(),
      passScore: this._config.passScore,
      passed: false,
      roundResults: this._results.slice(),
    };
  }

  /** @param {boolean} correct @param {string} correctId @param {string} selectedId @returns {L2BTestState} */
  _buildFeedbackState(correct, correctId, selectedId) {
    return {
      mode: "feedback",
      targetItem: this._currentTarget,
      tiles: this._currentTiles.slice(),
      wasCorrect: correct,
      correctConceptId: correctId,
      selectedConceptId: selectedId,
      currentRound: this._roundIndex,
      totalRounds: this._testList.length,
      correctCount: this._correctCount,
      errorCount: this._errorCount,
      accuracyPercent: this._accuracy(),
      passScore: this._config.passScore,
      passed: false,
      roundResults: this._results.slice(),
    };
  }

  /** @returns {L2BTestState} */
  _buildCompleteState() {
    const total = this._testList.length;
    const accuracy = total > 0 ? (this._correctCount / total) * 100 : 0;
    return {
      mode: "complete",
      targetItem: null,
      tiles: [],
      wasCorrect: false,
      correctConceptId: "",
      selectedConceptId: "",
      currentRound: total,
      totalRounds: total,
      correctCount: this._correctCount,
      errorCount: this._errorCount,
      accuracyPercent: accuracy,
      passScore: this._config.passScore,
      passed: accuracy >= this._config.passScore,
      roundResults: this._results.slice(),
    };
  }

  // ─── Helpers ──────────────────────────────────────────────

  _accuracy() {
    const seen = this._roundIndex + (this._currentTarget ? 1 : 0);
    const denom = Math.max(seen, this._results.length);
    return denom > 0 ? (this._correctCount / denom) * 100 : 0;
  }

  /**
   * @template T
   * @param {T[]} list
   */
  static _shuffle(list) {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = list[i]; list[i] = list[j]; list[j] = tmp;
    }
  }

  /**
   * @param {L2BTestState} state
   * @returns {L2BTestState}
   */
  _publish(state) {
    this._lastState = state;
    this.dispatchEvent(new CustomEvent("stateChanged", { detail: state }));
    if (state.mode === "complete") this.dispatchEvent(new Event("completed"));
    return state;
  }
}
